/**
 * GET /type_curve_headers/:type_curve_header_public_id/workspaces
 *
 * With `?kinds=` the body is one flat `workspaces` array carrying every requested kind (each row
 * naming its own `kind`) plus a `count`. Without the param it is the Type Curve version family in
 * its original shape. `results`/`totalCount` are tolerated as legacy fallbacks.
 */
export interface TypeCurveWorkspacesListResponse {
  workspaces?: TypeCurveWorkspaceResponse[];
  count?: number;
  totalCount?: number;
  results?: TypeCurveWorkspaceResponse[];
}

/**
 * Normalize GET index (or future snake_case / alternate keys) into a flat list.
 * @param {unknown} data Raw API payload (object with results/workspaces, or an array).
 * @return {Array<TypeCurveWorkspaceResponse>} List of workspace records.
 */
export function workspacesFromListPayload(
  data: unknown
): TypeCurveWorkspaceResponse[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  if ('error' in data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data as TypeCurveWorkspaceResponse[];
  }
  const list = data as TypeCurveWorkspacesListResponse;
  const results = list.workspaces ?? list.results;
  if (Array.isArray(results)) {
    return results;
  }
  return [];
}

/** POST /type_curves/:type_curve_header_id/workspaces */
export interface TypeCurveWorkspaceVerificationItemHref {
  events: string | null;
  comments: string | null;
}

export interface TypeCurveWorkspaceResourceLink {
  href: string | null;
  auth?: string[];
}

export interface TypeCurveWorkspaceResourceGroup {
  events?: TypeCurveWorkspaceResourceLink | null;
  comments?: TypeCurveWorkspaceResourceLink | null;
}

export interface TypeCurveWorkspaceVerificationItem {
  status: string | null;
  id: number | null;
  commentsCount: number | null;
  href: TypeCurveWorkspaceVerificationItemHref;
}

export interface TypeCurveWorkspaceResponse {
  /** Present only in the `?kinds=` listing; names which listing section the row belongs to. Absent
   *  on the rich no-`kinds` family response. */
  kind?: 'type_curve' | 'dca' | 'inventory';
  /** Self URL for show / update (from workspace JSON). */
  href?: string;
  auth: string[];
  id: number;
  typeCurveHeaderId?: number;
  name: string;
  notes?: string | null;
  promoted: boolean;
  promotedAt: string | null;
  tasks: {
    completed: number;
    total: number;
  };
  /** Flat verdict on the compact `?kinds=` row (null for a DCA row, or when unverified). The rich
   *  family row carries this nested under `verifications` instead. */
  verificationStatus?: string | null;
  /** Compact `?kinds=` row only. On an inventory row, true when the workspace was launched from this
   *  type curve and false when it is listed because one of its zones resolves here; null for the
   *  other kinds, which have no launch family. */
  launchedHere?: boolean | null;
  /** The Type Curve currently holding this one's (scenario, area, formation) default, or null when
   *  the zone has none yet. Read-only, and only used to tell the operator what Set as Default would
   *  displace. Facts rather than a display string, so the label is composed in the modal. */
  zoneDefault: {
    typeCurveHeaderDetailsId: number;
    uniqueTcId: string | null;
    tcPublicId: string | null;
    /** Workspace name of the incumbent, the "Base" in "Base — 1,320 ft". */
    name: string | null;
    wellSpacingFt: number | null;
    /** True when the incumbent is an earlier version of this same Type Curve. */
    sameFamily: boolean;
  } | null;
  /** Nested verdicts on the rich no-`kinds` family row. Absent on the compact `?kinds=` row, which
   *  carries the flat `verificationStatus` above instead. */
  verifications?: {
    typeCurveWorkspace: TypeCurveWorkspaceVerificationItem;
    unitResolution?: TypeCurveWorkspaceVerificationItem;
    inventoryVerification?: TypeCurveWorkspaceVerificationItem;
  };
  resources?: {
    typeCurveWorkspace?: TypeCurveWorkspaceResourceGroup | null;
    unitResolution?: TypeCurveWorkspaceResourceGroup | null;
    inventoryVerification?: TypeCurveWorkspaceResourceGroup | null;
    events?: TypeCurveWorkspaceResourceLink | null;
    comments?: TypeCurveWorkspaceResourceLink | null;
  };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Workspace entity URL for GET/PATCH (from `href` on the workspace record).
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Workspace JSON.
 * @return {string | null} Self path or null when missing.
 */
export function tcWorkspaceSelfHref(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): string | null {
  const href = (workspace as Record<string, unknown>).href;
  return typeof href === 'string' && href.length > 0 ? href : null;
}

/**
 * Reads the events/comments hrefs for a verification item from the nested
 * `resources` block — `resources.typeCurveWorkspace` / `resources.unitResolution`
 * (accepting camelCase or snake_case keys).
 * @param {Record<string, unknown>} workspace Raw workspace from API.
 * @param {string} camelKey camelCase resource key.
 * @param {string} snakeKey snake_case resource key.
 * @return {object} The resolved `events` and `comments` hrefs (or null).
 */
function nestedResourceHrefs(
  workspace: Record<string, unknown>,
  camelKey: string,
  snakeKey: string
): { events: string | null; comments: string | null } {
  const resources = workspace.resources as Record<string, unknown> | undefined;
  const block =
    (resources?.[camelKey] as Record<string, unknown> | undefined) ??
    (resources?.[snakeKey] as Record<string, unknown> | undefined);
  const events = (block?.events as { href?: unknown } | undefined)?.href;
  const comments = (block?.comments as { href?: unknown } | undefined)?.href;
  return {
    events: typeof events === 'string' ? events : null,
    comments: typeof comments === 'string' ? comments : null,
  };
}

/**
 * Workspace JSON from Rails uses snake_case (`type_curve_workspace`, `comments_count`, etc.).
 * This normalizes the type-curve verification block for UI and API wiring.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {TypeCurveWorkspaceVerificationItem | null} Normalized verification or null.
 */
export function getTcWorkspaceTypeCurveVerification(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): TypeCurveWorkspaceVerificationItem | null {
  const w = workspace as Record<string, unknown>;
  const verifications = w.verifications as Record<string, unknown> | undefined;
  if (!verifications) {
    return null;
  }
  const raw =
    (verifications.typeCurveWorkspace as Record<string, unknown> | undefined) ??
    (verifications.type_curve_workspace as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  // Backend nests these under `resources.typeCurveWorkspace`; fall back to the
  // old flat `resources.events`/`resources.comments` and the legacy per-item
  // `href` for older responses.
  const nested = nestedResourceHrefs(
    w,
    'typeCurveWorkspace',
    'type_curve_workspace'
  );
  const flat = w.resources as Record<string, unknown> | undefined;
  const flatEvents = (flat?.events as { href?: unknown } | undefined)?.href;
  const flatComments = (flat?.comments as { href?: unknown } | undefined)?.href;
  const hrefRaw = raw.href as Record<string, unknown> | undefined;

  const eventsHref =
    nested.events ??
    (typeof flatEvents === 'string' ? flatEvents : null) ??
    (typeof hrefRaw?.events === 'string' ? hrefRaw.events : null);
  const commentsHref =
    nested.comments ??
    (typeof flatComments === 'string' ? flatComments : null) ??
    (typeof hrefRaw?.comments === 'string' ? hrefRaw.comments : null);

  const id = raw.id;
  const status = raw.status;

  const commentsCount =
    typeof raw.commentsCount === 'number'
      ? raw.commentsCount
      : typeof raw.comments_count === 'number'
        ? raw.comments_count
        : null;

  return {
    status: status === null || status === undefined ? null : String(status),
    id: typeof id === 'number' ? id : null,
    commentsCount,
    href: { events: eventsHref, comments: commentsHref },
  };
}

/**
 * Events POST URL for the workspace type-curve verification item.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {string | null} Path such as `/verification_items/:id/events`, or null.
 */
export function tcWorkspaceVerificationEventsHref(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): string | null {
  const events = getTcWorkspaceTypeCurveVerification(workspace)?.href?.events;
  return events && events.length > 0 ? events : null;
}

/**
 * Normalizes the `unit_resolution` verification block (mirrors
 * `getTcWorkspaceTypeCurveVerification`). Its events/comments hrefs come from
 * `resources.unitResolution` (falling back to the block's own `href`). Returns
 * null until the backend exposes the block.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {TypeCurveWorkspaceVerificationItem | null} Normalized verification or null.
 */
export function getTcWorkspaceUnitResolutionVerification(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): TypeCurveWorkspaceVerificationItem | null {
  const w = workspace as Record<string, unknown>;
  const verifications = w.verifications as Record<string, unknown> | undefined;
  if (!verifications) {
    return null;
  }
  const raw =
    (verifications.unitResolution as Record<string, unknown> | undefined) ??
    (verifications.unit_resolution as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const nested = nestedResourceHrefs(w, 'unitResolution', 'unit_resolution');
  const hrefRaw = raw.href as Record<string, unknown> | undefined;
  const eventsHref =
    nested.events ??
    (typeof hrefRaw?.events === 'string' ? hrefRaw.events : null);
  const commentsHref =
    nested.comments ??
    (typeof hrefRaw?.comments === 'string' ? hrefRaw.comments : null);

  const id = raw.id;
  const status = raw.status;
  const commentsCount =
    typeof raw.commentsCount === 'number'
      ? raw.commentsCount
      : typeof raw.comments_count === 'number'
        ? raw.comments_count
        : null;

  return {
    status: status === null || status === undefined ? null : String(status),
    id: typeof id === 'number' ? id : null,
    commentsCount,
    href: { events: eventsHref, comments: commentsHref },
  };
}

/**
 * Events POST URL for the workspace unit-resolution verification item.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {string | null} Path such as `/verification_items/:id/events`, or null.
 */
export function tcWorkspaceUnitResolutionEventsHref(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): string | null {
  const events =
    getTcWorkspaceUnitResolutionVerification(workspace)?.href?.events;
  return events && events.length > 0 ? events : null;
}

/**
 * Normalizes the `inventory_verification` verification block (mirrors
 * `getTcWorkspaceUnitResolutionVerification`). Its events/comments hrefs come
 * from `resources.inventoryVerification` (falling back to the block's own
 * `href`). Returns null until the backend exposes the block.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {TypeCurveWorkspaceVerificationItem | null} Normalized verification or null.
 */
export function getTcWorkspaceInventoryVerification(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): TypeCurveWorkspaceVerificationItem | null {
  const w = workspace as Record<string, unknown>;
  const verifications = w.verifications as Record<string, unknown> | undefined;
  if (!verifications) {
    return null;
  }
  const raw =
    (verifications.inventoryVerification as
      Record<string, unknown> | undefined) ??
    (verifications.inventory_verification as
      Record<string, unknown> | undefined);
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const nested = nestedResourceHrefs(
    w,
    'inventoryVerification',
    'inventory_verification'
  );
  const hrefRaw = raw.href as Record<string, unknown> | undefined;
  const eventsHref =
    nested.events ??
    (typeof hrefRaw?.events === 'string' ? hrefRaw.events : null);
  const commentsHref =
    nested.comments ??
    (typeof hrefRaw?.comments === 'string' ? hrefRaw.comments : null);

  const id = raw.id;
  // The backend serializes inventory_verification_item.correctness_state as a
  // raw boolean (true/false/nil) unlike unit_resolution / type_curve_workspace
  // which go through verification_status_for() and return 'correct'/'incorrect'.
  // Normalize to the string convention the rest of the frontend expects.
  const rawStatus = raw.status;
  const status =
    rawStatus === true
      ? 'correct'
      : rawStatus === false
        ? 'incorrect'
        : rawStatus === null || rawStatus === undefined
          ? null
          : String(rawStatus);
  const commentsCount =
    typeof raw.commentsCount === 'number'
      ? raw.commentsCount
      : typeof raw.comments_count === 'number'
        ? raw.comments_count
        : null;

  return {
    status,
    id: typeof id === 'number' ? id : null,
    commentsCount,
    href: { events: eventsHref, comments: commentsHref },
  };
}

/**
 * Events POST URL for the workspace inventory verification item.
 * @param {TypeCurveWorkspaceResponse | Record<string, unknown>} workspace Raw workspace from API.
 * @return {string | null} Path such as `/verification_items/:id/events`, or null.
 */
export function tcWorkspaceInventoryVerificationEventsHref(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): string | null {
  const events = getTcWorkspaceInventoryVerification(workspace)?.href?.events;
  return events && events.length > 0 ? events : null;
}

export function tcWorkspaceNumericId(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): number | undefined {
  const id = (workspace as Record<string, unknown>).id;
  return typeof id === 'number' ? id : undefined;
}

export function tcWorkspaceTypeCurveHeaderId(
  workspace: TypeCurveWorkspaceResponse | Record<string, unknown>
): number | undefined {
  const w = workspace as Record<string, unknown>;
  const id = w.typeCurveHeaderId ?? w.type_curve_header_id;
  return typeof id === 'number' ? id : undefined;
}
